import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfidenceSlider } from "@rapid/components/ConfidenceSlider";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { useCurrentMedia } from "@rapid/store";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";

interface AdjustPredictionsActionCardProps {
    disabled?: boolean;
}

export const AdjustPredictionsActionCard = ({ disabled }: AdjustPredictionsActionCardProps) => {
    const { currentFramePredictions } = useComputedValues();
    const currentMedia = useCurrentMedia();
    const { updateMediaItem } = useMediaUtils();

    const [confidenceThreshold, setConfidenceThreshold] = useState(
        currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD
    );

    useEffect(() => {
        setConfidenceThreshold(
            currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD
        );
    }, [currentMedia?.file.confidenceThreshold, currentMedia?.file.id]);

    const updateConfidenceThreshold = useCallback(
        (newThreshold: number) => {
            if (!currentMedia?.file) return;

            setConfidenceThreshold(newThreshold);
            updateMediaItem({ ...currentMedia.file, confidenceThreshold: newThreshold });
        },
        [currentMedia?.file, updateMediaItem]
    );

    const visiblePredictionCount = useMemo(() => {
        if (!Array.isArray(currentFramePredictions)) return 0;
        return currentFramePredictions.filter((pred) => pred.confidence >= confidenceThreshold)
            .length;
    }, [currentFramePredictions, confidenceThreshold]);

    return (
        <div
            className={twClassNames(
                "overflow-hidden rounded-lg border border-gray-300 bg-gray-50",
                disabled && "pointer-events-none opacity-50"
            )}
        >
            <ConfidenceSlider
                className="gap-2 px-4 py-2.5"
                initialConfidenceThreshold={confidenceThreshold}
                onConfidenceThresholdChanged={updateConfidenceThreshold}
                predictionCount={visiblePredictionCount}
                showCount={false}
            />
        </div>
    );
};
